import React, { useState } from 'react';
import { Alert, Button, Card, Collapse, Descriptions, Divider, Input, Select, Space, Table, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getMonitorApiBaseUrl } from '../../config/env';
import { formatDateTime } from '../../utils/format';
import { getBackendLogs } from './api';
import { BackendErrorEvent, BackendLogKind, TimeFilter } from './types';
import { DEFAULT_REFRESH_INTERVAL_MS, REFRESH_INTERVAL_OPTIONS, useTimeWindows } from './queries';

interface Props {
  title?: string;
  children?: React.ReactNode;
  kind: BackendLogKind;
  service: string;
  refresh?: number;
  autoRefresh?: boolean;
}

function todayFilter(): TimeFilter {
  const now = new Date();
  return { from: new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())).toISOString(), to: now.toISOString() };
}

function groupBackendLogs(items: BackendErrorEvent[]) {
  const groups = new Map<string, BackendErrorEvent[]>();
  items.forEach((item, index) => {
    const key = item.request_id ? JSON.stringify([item.service, item.request_id]) : `event:${index}`;
    const events = groups.get(key);
    if (events) events.push(item);
    else groups.set(key, [item]);
  });
  return Array.from(groups, ([key, events]) => {
    const requestIndex = events.findIndex(event => /^[A-Z]+ \//.test(event.message));
    const primaryIndex = requestIndex < 0 ? 0 : requestIndex;
    return { ...events[primaryIndex], key, events: [events[primaryIndex], ...events.filter((_, index) => index !== primaryIndex)] };
  });
}

const formatLogValue = (value: unknown): string => {
  if (typeof value !== 'string') return JSON.stringify(value, null, 2) ?? String(value);

  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};

const LogDetails: React.FC<{ event: BackendErrorEvent }> = ({ event }) => {
  const { request_payload, response_payload, ...details } = event.details || {};
  const fields = Object.entries({
    service: event.service,
    request_id: event.request_id,
    status: event.status,
    latency_ms: event.latency_ms,
    ...details,
  }).filter(([, value]) => value !== null && value !== undefined && value !== '');
  const payloads = [
    { key: 'request', label: 'Request payload', value: request_payload },
    { key: 'response', label: 'Response payload', value: response_payload },
  ].filter(({ value }) => value !== null && value !== undefined && value !== '');

  return <Space direction="vertical" size={12} className="w-full">
    <Descriptions size="small" bordered column={1} items={fields.map(([key, value]) => ({
      key, label: key, children: <span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{formatLogValue(value)}</span>,
    }))} />
    {payloads.length > 0 && <Collapse size="small" items={payloads.map(({ key, label, value }) => ({
      key, label, children: <pre style={{ margin: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxHeight: 360, overflow: 'auto' }}>{formatLogValue(value)}</pre>,
    }))} />}
  </Space>;
};

const LogPage: React.FC<Props> = ({ kind, service, autoRefresh, title, children }) => {
  const baseURL = getMonitorApiBaseUrl();
  const windows = useTimeWindows();
  const [windowID, setWindowID] = useState('today');
  const [appliedWindow, setAppliedWindow] = useState('today');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [validation, setValidation] = useState('');
  const [filter, setFilter] = useState<TimeFilter>(todayFilter);
  const [refresh, setRefresh] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [refreshInterval, setRefreshInterval] = useState(DEFAULT_REFRESH_INTERVAL_MS);
  const query = useInfiniteQuery({
    queryKey: ['monitor-backend-logs', baseURL, kind, filter, service, appliedWindow, refresh, pageSize],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ signal, pageParam }) => getBackendLogs(kind, appliedWindow === 'today' && !pageParam ? todayFilter() : filter, service, pageParam, pageSize, signal),
    getNextPageParam: page => page.has_more && page.next_cursor ? page.next_cursor : undefined,
    enabled: Boolean(baseURL), retry: false, staleTime: Infinity,
    refetchOnWindowFocus: false, refetchOnReconnect: false,
    refetchInterval: state => autoRefresh && (state.state.data?.pages.length ?? 1) === 1 ? refreshInterval || false : false,
  });
  const items = query.data?.pages.flatMap(page => page.items) || [];
  const rows = groupBackendLogs(items);
  const restart = () => setRefresh(value => value + 1);
  const apply = () => {
    let next: TimeFilter;
    if (windowID === 'custom') {
      const start = new Date(`${from}Z`);
      const end = new Date(`${to}Z`);
      if (!from || !to || !Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || start >= end) {
        setValidation('Choose valid UTC dates with the start before the end.');
        return;
      }
      if (end.getTime() - start.getTime() > 30 * 86400000) {
        setValidation('Choose a range of at most 30 days.');
        return;
      }
      next = { from: start.toISOString(), to: end.toISOString() };
    } else next = windowID === 'today' ? todayFilter() : { window: windowID };
    setValidation('');
    setAppliedWindow(windowID);
    setFilter(next);
    setRefresh(value => value + 1);
  };
  if (!baseURL) return <Alert type="info" showIcon message="Monitor API is not configured for this environment" />;
  const controls = <Space wrap align="end">
      <label>Time window (UTC)
        <Select aria-label={`${kind} log time window`} className="block" style={{ width: 190 }} value={windowID}
          onChange={setWindowID} loading={windows.isLoading} options={[
            { value: 'today', label: 'Today' },
            ...(windows.data?.windows.map(({ id, label }) => ({ value: id, label })) || []),
            { value: 'custom', label: 'Custom date range' },
          ]} />
      </label>
      {windowID === 'custom' && <>
        <label>From (UTC)<Input type="datetime-local" aria-label={`${kind} logs from`} value={from} onChange={event => setFrom(event.target.value)} /></label>
        <label>To (UTC)<Input type="datetime-local" aria-label={`${kind} logs to`} value={to} onChange={event => setTo(event.target.value)} /></label>
      </>}
      <Button type="primary" onClick={apply}>Apply range</Button>
      {autoRefresh && <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>Auto refresh
        <Select aria-label={`${title || service} refresh interval`} style={{ width: 100 }}
          value={refreshInterval} onChange={setRefreshInterval} options={REFRESH_INTERVAL_OPTIONS} />
      </label>}
      <Button icon={<ReloadOutlined />} loading={query.isFetching} onClick={restart}>
        Refresh
      </Button>
    </Space>;
  const content = <Space direction="vertical" size="middle" className="w-full">
    {children}
    {controls}
    {autoRefresh && (query.data?.pages.length ?? 0) > 1 && <Typography.Text type="secondary">
      Auto refresh paused while viewing older logs. Refresh latest to resume.
    </Typography.Text>}
    {validation && <Alert type="error" showIcon message={validation} />}
    {windows.isError && <Alert type="warning" showIcon message="Unable to load preset windows"
      action={<Button onClick={() => windows.refetch()}>Retry windows</Button>} />}
    {query.isError && <Alert type="error" showIcon message="Unable to load backend logs"
      description={query.error instanceof Error ? query.error.message : 'Monitor request failed'}
      action={<Button onClick={() => query.isFetchNextPageError ? query.fetchNextPage() : query.refetch()}>Retry</Button>} />}
    <Table size="small" loading={query.isFetching && !query.isFetchingNextPage} pagination={false} scroll={{ x: 1000 }}
      locale={{ emptyText: query.isError ? 'Logs unavailable' : 'No logs in this time range' }}
      dataSource={rows}
      columns={[
        { title: 'Time (UTC)', dataIndex: 'time', render: formatDateTime, width: 180 },
        { title: 'Level', dataIndex: 'level', width: 90 },
        { title: 'Message / route', dataIndex: 'message' },
        { title: 'Status', dataIndex: 'status', width: 80 },
        { title: 'Request ID', dataIndex: 'request_id', width: 220 },
      ]}
      expandable={{ expandedRowRender: (item) => <div className="w-full">
        {item.events.map((event, index) => <React.Fragment key={index}>
          {index > 0 && <Divider style={{ margin: '8px 0' }} />}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16, minWidth: 0 }}>
            <div style={{ overflowWrap: 'anywhere' }}>
              <Typography.Text strong>{event.message}</Typography.Text>
              <div><Typography.Text type="secondary">{formatDateTime(event.time)} UTC · {event.level} · Source: {event.source || '—'}</Typography.Text></div>
            </div>
            {event.error && <pre style={{ margin: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{event.error}</pre>}
            <LogDetails event={event} />
            {(event.stack || event.panic_stack) && <Collapse size="small" defaultActiveKey={[]} items={[{
              key: 'stack', label: 'Stack trace', children: <pre style={{ margin: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxHeight: 360, overflow: 'auto' }}>
                {[event.stack, event.panic_stack].filter(Boolean).join('\n\n')}
              </pre>,
            }]} />}
          </div>
        </React.Fragment>)}
      </div> }} />
    <Space wrap align="center" className="w-full" style={{ justifyContent: 'center' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>Page size
        <Select aria-label={`${kind} log page size`} className="block" style={{ width: 90 }} value={pageSize}
          options={[10, 20, 50, 100, 200].map(value => ({ value, label: String(value) }))}
          onChange={value => { setPageSize(value); restart(); }} />
      </label>
      <Typography.Text>{rows.length} rows · {items.length} log events loaded</Typography.Text>
      {query.hasNextPage && <Button loading={query.isFetchingNextPage} disabled={query.isFetching && !query.isFetchingNextPage}
        onClick={() => query.fetchNextPage()}>Load more</Button>}
    </Space>
  </Space>;
  return title ? <Card title={title} size="small">{content}</Card> : content;
};

export const BackendLogsPanel: React.FC<Props> = (props) => <LogPage
  key={JSON.stringify([getMonitorApiBaseUrl(), props.kind, props.service, props.refresh])} {...props} />;
